import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { LOCALES, LOCALE_NAMES } from "@/lib/i18n";

export const Route = createFileRoute("/admin/categories")({
  staticData: { sitemap: false },
  component: CategoriesAdmin,
});

type Draft = {
  id?: string;
  slug: string;
  names: Record<string, string>;
  sort_order: number;
  count: number;
};

function CategoriesAdmin() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-blog-categories"],
    queryFn: async () => {
      const [{ data: cats, error }, { data: links }] = await Promise.all([
        supabase.from("blog_categories").select("*").order("sort_order"),
        supabase.from("content_item_categories").select("category_id"),
      ]);
      if (error) throw error;
      return (cats ?? []).map((c) => ({
        id: c.id,
        slug: c.slug,
        names: (c.names ?? {}) as Record<string, string>,
        sort_order: c.sort_order,
        count: (links ?? []).filter((l) => l.category_id === c.id).length,
      }));
    },
  });
  const [rows, setRows] = useState<Draft[]>([]);
  useEffect(() => {
    if (data) setRows(data);
  }, [data]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin-blog-categories"] });
  };

  const save = useMutation({
    mutationFn: async (row: Draft) => {
      const slug = row.slug.trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-");
      if (!slug || !row.names["en"]?.trim()) throw new Error("Slug and English name are required");
      const payload = { slug, names: row.names, sort_order: row.sort_order };
      const { error } = row.id
        ? await supabase.from("blog_categories").update(payload).eq("id", row.id)
        : await supabase.from("blog_categories").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Saved");
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("blog_categories").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Deleted");
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const patch = (i: number, p: Partial<Draft>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...p } : r)));

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-6 animate-spin text-primary" aria-hidden />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Blog categories</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Names per language; empty languages fall back to English. Assign categories in each
            blog article.
          </p>
        </div>
        <Button
          onClick={() =>
            setRows((prev) => [
              ...prev,
              { slug: "", names: {}, sort_order: prev.length + 1, count: 0 },
            ])
          }
        >
          <Plus className="size-4" aria-hidden />
          Add category
        </Button>
      </div>

      <div className="mt-8 grid gap-4">
        {rows.map((row, i) => (
          <div key={row.id ?? `new-${i}`} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex flex-wrap items-end gap-3">
              <label className="grid gap-1 text-xs text-muted-foreground">
                Slug
                <Input
                  value={row.slug}
                  onChange={(e) => patch(i, { slug: e.target.value })}
                  className="w-56"
                />
              </label>
              <label className="grid gap-1 text-xs text-muted-foreground">
                Order
                <Input
                  type="number"
                  value={row.sort_order}
                  onChange={(e) => patch(i, { sort_order: Number(e.target.value) || 0 })}
                  className="w-24"
                />
              </label>
              <span className="text-sm text-muted-foreground">{row.count} articles</span>
              <div className="ms-auto flex gap-2">
                <Button size="sm" onClick={() => save.mutate(row)} disabled={save.isPending}>
                  <Save className="size-4" aria-hidden />
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Delete category"
                  onClick={() => {
                    if (!row.id) return setRows((prev) => prev.filter((_, idx) => idx !== i));
                    if (confirm(`Delete "${row.names["en"] || row.slug}"?`)) remove.mutate(row.id);
                  }}
                >
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              </div>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {LOCALES.map((code) => (
                <label key={code} className="grid gap-1 text-xs text-muted-foreground">
                  {LOCALE_NAMES[code]}
                  <Input
                    value={row.names[code] ?? ""}
                    onChange={(e) => patch(i, { names: { ...row.names, [code]: e.target.value } })}
                  />
                </label>
              ))}
            </div>
          </div>
        ))}
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No categories yet.</p>
        ) : null}
      </div>
    </>
  );
}
